import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaPlus, FaFileUpload, FaPaperPlane, FaSpinner,
  FaCheckCircle, FaClock, FaSearch, FaTools, FaTimesCircle,
  FaFilePdf, FaTrash, FaEye
} from 'react-icons/fa';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: FaClock },
  reviewing: { label: 'En examen', color: 'bg-blue-100 text-blue-800', icon: FaSearch },
  approved: { label: 'Approuvé', color: 'bg-green-100 text-green-800', icon: FaCheckCircle },
  in_development: { label: 'En développement', color: 'bg-purple-100 text-purple-800', icon: FaTools },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-800', icon: FaCheckCircle },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-800', icon: FaTimesCircle }
};

const CourseRequestPage = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    targetAudience: 'students',
    urgency: 'medium',
    additionalNotes: ''
  });
  const [pdfFile, setPdfFile] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/api/course-requests/');
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Erreur chargement demandes:', error);
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Seuls les fichiers PDF sont acceptés');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10MB)');
        return;
      }
      setPdfFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Veuillez remplir le titre et la description');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('targetAudience', formData.targetAudience);
      formDataToSend.append('urgency', formData.urgency);
      formDataToSend.append('additionalNotes', formData.additionalNotes);
      
      if (pdfFile) {
        formDataToSend.append('pdf_file', pdfFile);
      }
      
      await api.post('/api/course-requests/', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Demande envoyée avec succès !');
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        targetAudience: 'students',
        urgency: 'medium',
        additionalNotes: ''
      });
      setPdfFile(null);
      fetchRequests();
      
    } catch (error) {
      console.error('Erreur soumission:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#1B4965]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1B4965] to-[#2D5A7B] text-white py-6 px-4">
        <div className="container mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <FaArrowLeft /> Retour
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Demandes de cours</h1>
              <p className="text-white/70 mt-1">Demandez la création de cours personnalisés</p>
            </div>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-[#E63946] hover:bg-[#C1121F]"
              data-testid="new-course-request-btn"
            >
              <FaPlus className="mr-2" /> Nouvelle demande
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Liste des demandes */}
        {requests.length === 0 && !showForm ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="text-6xl mb-4">📚</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucune demande</h3>
              <p className="text-gray-500 mb-6">
                Vous n'avez pas encore fait de demande de cours personnalisé.
              </p>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-[#1B4965] hover:bg-[#15384F]"
              >
                <FaPlus className="mr-2" /> Faire une demande
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {requests.map((request) => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedRequest(request)}
                  data-testid={`course-request-${request.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-[#1B4965]">{request.title}</h3>
                          <StatusBadge status={request.status} />
                        </div>
                        <p className="text-gray-600 text-sm line-clamp-2">{request.description}</p>
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                          <span>
                            {new Date(request.createdAt).toLocaleDateString('fr-FR', {
                              day: 'numeric', month: 'long', year: 'numeric'
                            })}
                          </span>
                          {request.pdfFilename && (
                            <span className="flex items-center gap-1 text-red-600">
                              <FaFilePdf /> PDF joint
                            </span>
                          )}
                          {request.estimatedDelivery && (
                            <span className="text-green-600">
                              Livraison : {request.estimatedDelivery}
                            </span>
                          )}
                        </div>
                      </div>
                      <FaEye className="text-gray-400 ml-4" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Modal Formulaire */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowForm(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <h2 className="text-2xl font-bold text-[#1B4965] mb-6">
                    Nouvelle demande de cours
                  </h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Titre */}
                    <div>
                      <Label htmlFor="title">Titre du cours souhaité *</Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="Ex: Initiation au data journalisme"
                        required
                        data-testid="course-title-input"
                      />
                    </div>
                    
                    {/* Description */}
                    <div>
                      <Label htmlFor="description">Description détaillée *</Label>
                      <Textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Décrivez le contenu souhaité, les objectifs pédagogiques, les compétences à acquérir..."
                        rows={5}
                        required
                        data-testid="course-description-input"
                      />
                    </div>
                    
                    {/* Public cible & Urgence */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="targetAudience">Public cible</Label>
                        <select
                          id="targetAudience"
                          name="targetAudience"
                          value={formData.targetAudience}
                          onChange={handleChange}
                          className="w-full border rounded-lg px-3 py-2"
                          data-testid="course-audience-select"
                        >
                          <option value="students">Étudiants</option>
                          <option value="journalists">Journalistes</option>
                          <option value="both">Les deux</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="urgency">Niveau d'urgence</Label>
                        <select
                          id="urgency"
                          name="urgency"
                          value={formData.urgency}
                          onChange={handleChange}
                          className="w-full border rounded-lg px-3 py-2"
                          data-testid="course-urgency-select"
                        >
                          <option value="low">🟢 Basse</option>
                          <option value="medium">🟡 Moyenne</option>
                          <option value="high">🔴 Haute</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Notes additionnelles */}
                    <div>
                      <Label htmlFor="additionalNotes">Notes additionnelles</Label>
                      <Textarea
                        id="additionalNotes"
                        name="additionalNotes"
                        value={formData.additionalNotes}
                        onChange={handleChange}
                        placeholder="Informations complémentaires, références, exemples..."
                        rows={3}
                        data-testid="course-notes-input"
                      />
                    </div>
                    
                    {/* Upload PDF */}
                    <div>
                      <Label>Document PDF (optionnel)</Label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#1B4965] transition-colors">
                        {pdfFile ? (
                          <div className="flex items-center justify-center gap-3">
                            <FaFilePdf className="text-red-500 text-2xl" />
                            <span className="text-gray-700">{pdfFile.name}</span>
                            <button
                              type="button"
                              onClick={() => setPdfFile(null)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ) : (
                          <label className="cursor-pointer">
                            <FaFileUpload className="text-4xl text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-600">Cliquez pour ajouter un PDF</p>
                            <p className="text-xs text-gray-400 mt-1">Max 10MB</p>
                            <input
                              type="file"
                              accept=".pdf"
                              onChange={handleFileChange}
                              className="hidden"
                              data-testid="course-pdf-input"
                            />
                          </label>
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowForm(false)}
                        className="flex-1"
                      >
                        Annuler
                      </Button>
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 bg-[#E63946] hover:bg-[#C1121F]"
                        data-testid="submit-course-request-btn"
                      >
                        {submitting ? (
                          <FaSpinner className="animate-spin mr-2" />
                        ) : (
                          <FaPaperPlane className="mr-2" />
                        )}
                        Envoyer la demande
                      </Button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modal Détails */}
        <AnimatePresence>
          {selectedRequest && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedRequest(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-2xl font-bold text-[#1B4965]">
                      {selectedRequest.title}
                    </h2>
                    <StatusBadge status={selectedRequest.status} />
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-1">Description</h4>
                      <p className="text-gray-600 whitespace-pre-line">{selectedRequest.description}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Public cible :</span>
                        <span className="ml-2 font-medium">
                          {selectedRequest.targetAudience === 'students' ? 'Étudiants' :
                           selectedRequest.targetAudience === 'journalists' ? 'Journalistes' : 'Les deux'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Urgence :</span>
                        <span className="ml-2 font-medium">
                          {selectedRequest.urgency === 'low' ? '🟢 Basse' :
                           selectedRequest.urgency === 'high' ? '🔴 Haute' : '🟡 Moyenne'}
                        </span>
                      </div>
                    </div>
                    
                    {selectedRequest.additionalNotes && (
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-1">Notes</h4>
                        <p className="text-gray-600">{selectedRequest.additionalNotes}</p>
                      </div>
                    )}
                    
                    {selectedRequest.adminNotes && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-1">Réponse de l'équipe</h4>
                        <p className="text-blue-700">{selectedRequest.adminNotes}</p>
                      </div>
                    )}
                    
                    {selectedRequest.estimatedDelivery && (
                      <div className="bg-green-50 p-4 rounded-lg text-center">
                        <span className="text-green-700">
                          📅 Livraison estimée : <strong>{selectedRequest.estimatedDelivery}</strong>
                        </span>
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-400 pt-4 border-t">
                      Demande créée le {new Date(selectedRequest.createdAt).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <Button
                      onClick={() => setSelectedRequest(null)}
                      className="w-full"
                      variant="outline"
                    >
                      Fermer
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CourseRequestPage;
